import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import Loader from 'react-loader-spinner';
import { FiAward, FiSave, FiX } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { useChampionships } from '../utilities/championships';
import { authorizedPost } from '../utilities/stadiumsApi';
import { champFields, extractEntity, extractList as extractTournamentList } from '../utilities/tournamentsApi';
import '../styles/leagueChampionsManagement.css';
import { resetMobileViewport } from '../utilities/mobileViewport';

const parsePositiveId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** iOS Safari can leave a zoomed/cropped visual viewport after select-search closes. */
const resetMobileViewportAfterSelect = () => {
  resetMobileViewport();
};

const DEFAULT_PLACES = [1, 2, 3].map((place) => ({
  place,
  data: []
}));

function LeagueChampionsManagement() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [leagueChamps, setLeagueChamps] = useState(DEFAULT_PLACES);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    let cancelled = false;

    const hydrateFromQuery = async (nextCountries) => {
      const params = new URLSearchParams(location.search || '');
      const countryId = parsePositiveId(params.get('country'));
      const leagueId = parsePositiveId(params.get('league'));
      const seasonId = parsePositiveId(params.get('season'));
      if (!countryId) return;

      const country = findByValue(nextCountries, countryId);
      if (!country) return;
      setSelectedCountry(country);

      const nextLeagues = await loadLeaguesForCountry(country.value);
      if (cancelled || !leagueId) return;

      const league = findByValue(nextLeagues, leagueId);
      if (!league) return;
      setSelectedLeague(league);

      const nextSeasons = await loadSeasonsForLeague(league);
      if (cancelled || !seasonId) return;

      const season = findByValue(nextSeasons, seasonId);
      if (season) {
        setSelectedSeason(season);
      }
    };

    const initCascade = async () => {
      resetCascade();
      if (!champId) {
        setCountries([]);
        return;
      }
      const nextCountries = await loadCountries();
      if (!cancelled) {
        await hydrateFromQuery(nextCountries);
      }
    };

    initCascade();
    return () => {
      cancelled = true;
    };
  }, [champId, location.search]);

  useEffect(() => {
    if (selectedLeague && selectedSeason) {
      loadLeagueData();
    } else {
      setTeams([]);
      setLeagueChamps(DEFAULT_PLACES);
    }
  }, [selectedLeague, selectedSeason]);

  const findByValue = (items, value) => {
    return items.find((item) => String(item.value) === String(value));
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map((item) => item.item);
    };
  };

  const extractList = (payload, keys = []) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      if (Array.isArray(payload[key])) {
        return payload[key];
      }
      if (payload[key] && Array.isArray(payload[key].data)) {
        return payload[key].data;
      }
    }

    if (Array.isArray(payload.data)) {
      return payload.data;
    }

    return [];
  };

  const normalizeChampionPlaces = (championsData) => {
    const normalized = DEFAULT_PLACES.map((item) => ({ ...item }));

    championsData.forEach((item) => {
      const place = Number(item.place || item.position || item.rank || item.id);
      if (![1, 2, 3].includes(place)) {
        return;
      }

      const nestedTeam = item.data && item.data.length > 0 ? item.data[0] : null;
      const teamId = item.team_id || item.teamId || nestedTeam?.team_id || nestedTeam?.id || false;
      const teamName = item.team_name || item.teamName || nestedTeam?.team_name || nestedTeam?.name || '';

      normalized[place - 1] = {
        ...item,
        place,
        team_id: teamId,
        team_name: teamName,
        data: teamId ? [{ ...(nestedTeam || {}), team_id: teamId, name: teamName || nestedTeam?.name || '' }] : []
      };
    });

    return normalized;
  };

  const resetCascade = () => {
    setSelectedCountry(null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setLeagues([]);
    setSeasons([]);
    setTeams([]);
    setLeagueChamps(DEFAULT_PLACES);
    setMessage({ text: '', type: '' });
  };

  const mapSelectOptions = (items, getValue, getName) => (
    items
      .map((item, index) => {
        const value = Number(getValue(item));
        return {
          ...item,
          value,
          name: getName(item) || '',
          key: index,
        };
      })
      .filter((item) => item.value > 0)
  );

  const mapSeasons = (items) => mapSelectOptions(
    extractTournamentList(items),
    (item) => item.season_id || item.id,
    (item) => item.season_name || item.name
  );

  const loadCountries = async () => {
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      const nextCountries = mapSelectOptions(
        extractTournamentList(response.data),
        (item) => item.id,
        (item) => item.name
      );
      setCountries(nextCountries);
      return nextCountries;
    } catch (error) {
      console.error('Error loading countries:', error);
      setCountries([]);
      setMessage({ text: t('tsLoadCountriesError'), type: 'error' });
      return [];
    }
  };

  const loadLeaguesForCountry = async (countryId) => {
    if (!countryId) {
      setLeagues([]);
      return [];
    }

    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const nextLeagues = mapSelectOptions(
        extractTournamentList(extractEntity(response.data).leagues),
        (item) => item.id || item.league_id,
        (item) => item.name || item.league_name
      ).map((item) => ({
        ...item,
        seasons: extractTournamentList(item.seasons),
      }));
      setLeagues(nextLeagues);
      return nextLeagues;
    } catch (error) {
      console.error('Error loading leagues:', error);
      setLeagues([]);
      setMessage({ text: t('tsLoadLeaguesError'), type: 'error' });
      return [];
    }
  };

  const loadSeasonsForLeague = async (league) => {
    if (!league) {
      setSeasons([]);
      return [];
    }

    let nextSeasons = mapSeasons(league.seasons);
    if (!nextSeasons.length) {
      try {
        const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(league.value) });
        nextSeasons = mapSeasons(extractEntity(response.data).seasons);
      } catch (error) {
        console.error('Error loading seasons:', error);
        nextSeasons = [];
        setMessage({ text: t('tsLoadSeasonsError'), type: 'error' });
      }
    }

    setSeasons(nextSeasons);
    return nextSeasons;
  };

  const loadLeagueData = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      await Promise.all([loadLeagueTeams(), loadLeagueChampions()]);
    } catch (error) {
      setMessage({ text: t('lcLoadDataError'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadLeagueTeams = async () => {
    try {
      const response = await authorizedPost(dispatch, `/leagues/${selectedLeague.value}/teams`, {
        league_id: selectedLeague.value,
        season_id: selectedSeason.value,
      });

      const teamsData = extractList(response.data, ['teams']);

      setTeams(
        teamsData.map((item, index) => ({
          ...item,
          value: item.team_id || item.id,
          name: item.name || item.team_name,
          key: index,
        }))
      );
    } catch (error) {
      console.error('Error loading league teams:', error);
      setTeams([]);
      throw error;
    }
  };

  const loadLeagueChampions = async () => {
    try {
      const response = await authorizedPost(dispatch, `/leagues/${selectedLeague.value}/getLeagueChampions`, {
        league_id: selectedLeague.value,
        season_id: selectedSeason.value,
      });

      const championsData = extractList(response.data, ['champions', 'trophies']);
      setLeagueChamps(normalizeChampionPlaces(championsData));
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setLeagueChamps(DEFAULT_PLACES);
        return;
      }

      console.error('Error loading league champions:', error);
      setLeagueChamps(DEFAULT_PLACES);
      throw error;
    }
  };

  const handleCountryChange = async (countryId) => {
    const country = countryId ? findByValue(countries, countryId) : null;
    setSelectedCountry(country || null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setLeagues([]);
    setSeasons([]);
    setTeams([]);
    setLeagueChamps(DEFAULT_PLACES);
    setMessage({ text: '', type: '' });
    await loadLeaguesForCountry(country ? country.value : null);
    resetMobileViewportAfterSelect();
  };

  const handleLeagueChange = async (leagueId) => {
    const league = leagueId ? findByValue(leagues, leagueId) : null;
    setSelectedLeague(league || null);
    setSelectedSeason(null);
    setTeams([]);
    setLeagueChamps(DEFAULT_PLACES);
    setMessage({ text: '', type: '' });
    await loadSeasonsForLeague(league);
    resetMobileViewportAfterSelect();
  };

  const handleSeasonChange = (seasonId) => {
    const season = findByValue(seasons, seasonId);
    setSelectedSeason(season || null);
    setTeams([]);
    setLeagueChamps(DEFAULT_PLACES);
    setMessage({ text: '', type: '' });
    resetMobileViewportAfterSelect();
  };

  const getAssignedTeamId = (item) => {
    if (item.data && item.data.length > 0 && item.data[0].team_id) {
      return item.data[0].team_id;
    }

    return item.team_id || false;
  };

  const findDuplicatePlace = (teamId, currentPlace) => {
    if (!teamId) {
      return null;
    }

    return leagueChamps.find((item) => {
      return item.place !== currentPlace && String(getAssignedTeamId(item)) === String(teamId);
    });
  };

  const getDuplicateTeamIds = () => {
    const teamCounts = leagueChamps.reduce((accumulator, item) => {
      const teamId = getAssignedTeamId(item);

      if (!teamId) {
        return accumulator;
      }

      const normalizedTeamId = String(teamId);
      accumulator[normalizedTeamId] = (accumulator[normalizedTeamId] || 0) + 1;
      return accumulator;
    }, {});

    return Object.keys(teamCounts).filter((teamId) => teamCounts[teamId] > 1);
  };

  const handleChampionChange = (place, teamId) => {
    const selectedTeam = teamId ? findByValue(teams, teamId) : null;

    if (selectedTeam) {
      const duplicatePlace = findDuplicatePlace(selectedTeam.value, place);

      if (duplicatePlace) {
        setMessage({
          text: t('lcTeamAlreadyPlace', { name: selectedTeam.name, place: duplicatePlace.place }),
          type: 'error'
        });
        resetMobileViewportAfterSelect();
        return;
      }
    }

    setMessage({ text: '', type: '' });

    setLeagueChamps((prevState) => prevState.map((item) => {
      if (item.place !== place) {
        return item;
      }

      return {
        ...item,
        team_id: selectedTeam ? selectedTeam.value : false,
        team_name: selectedTeam ? selectedTeam.name : '',
        data: selectedTeam
          ? [{ team_id: selectedTeam.value, name: selectedTeam.name }]
          : []
      };
    }));
    resetMobileViewportAfterSelect();
  };

  const clearChampion = (place) => {
    handleChampionChange(place, '');
  };

  const getChampionTeamId = (item) => {
    return getAssignedTeamId(item) || '';
  };

  const getChampionTeamName = (item) => {
    const selectedTeamId = getChampionTeamId(item);
    const selectedTeam = selectedTeamId ? findByValue(teams, selectedTeamId) : null;
    return selectedTeam?.name || item.team_name || t('lcTeamNotSelected');
  };

  const handleSave = async () => {
    if (!selectedLeague || !selectedSeason) {
      setMessage({ text: t('lcSelectLeagueSeason'), type: 'error' });
      return;
    }

    if (getDuplicateTeamIds().length > 0) {
      setMessage({ text: t('lcDuplicateTeams'), type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const trophies = leagueChamps.map((it) => ({
        place: it.place,
        team_id: it.data && it.data.length && it.data[0] && it.data[0].team_id ? it.data[0].team_id : false
      }));

      await authorizedPost(dispatch, `/leagues/${selectedLeague.value}/add_trophies`, {
        league_id: selectedLeague.value,
        season_id: selectedSeason.value,
        trophies,
      });

      setMessage({ text: t('lcSaved'), type: 'success' });
      await loadLeagueChampions();
    } catch (error) {
      console.error('Error saving league champions:', error);
      setMessage({ text: t('lcSaveError'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const teamOptions = [{ value: '', name: t('lcNotSelected'), key: 'empty-team-option' }, ...teams];
  const placeLabel = (place) => t(`lcPlace${place}`);

  return (
    <div className="layoutWrapper">
      <div className="matchCenterWrapper league-champions-page">
        <div className="matchCenterHeader">
          <div className="league-champions-header">
            <div className="league-champions-header-text">
              <span className="matchCenterName">{t('navChampions')}</span>
              <p>{t('lcSubtitle')}</p>
            </div>

            {selectedLeague && selectedSeason && (
              <button
                type="button"
                className="league-champions-save-btn"
                onClick={handleSave}
                disabled={saving || loading}
              >
                <FiSave />
                {saving ? t('stSaving') : t('save')}
              </button>
            )}
          </div>

          <div className="league-champions-filters">
            <div className="league-champions-filter-group">
              <label>{t('gmCountry')}</label>
              <SelectSearch
                options={countries}
                search
                disabled={!champId}
                value={selectedCountry?.value}
                onChange={handleCountryChange}
                filterOptions={fuzzySearch}
                emptyMessage={t('cmCountryNotFound')}
                placeholder={t('cmCountryPh')}
              />
            </div>

            {selectedCountry ? (
              <div className="league-champions-filter-group">
                <label>{t('league')}</label>
                <SelectSearch
                  options={leagues}
                  search
                  value={selectedLeague?.value}
                  onChange={handleLeagueChange}
                  filterOptions={fuzzySearch}
                  emptyMessage={t('leagueNotFound')}
                  placeholder={t('leaguePlaceholder')}
                />
              </div>
            ) : null}

            {selectedLeague ? (
              <div className="league-champions-filter-group">
                <label>{t('stSeason')}</label>
                <SelectSearch
                  options={seasons}
                  search
                  value={selectedSeason?.value}
                  onChange={handleSeasonChange}
                  filterOptions={fuzzySearch}
                  emptyMessage={t('stSeasonNotFound')}
                  placeholder={t('stSeasonPlaceholder')}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="league-champions-container">
          {!champId && !champsLoading ? (
            <div className="league-champions-message error">{t('gmNeedChamp')}</div>
          ) : null}

          {message.text && (
            <div className={`league-champions-message ${message.type === 'success' ? 'success' : 'error'}`}>
              {message.text}
            </div>
          )}

          {loading ? (
            <div className="league-champions-loading">
              <Loader type="Rings" color="#3ad1a0" height={50} width={50} />
              <span>{t('lcLoading')}</span>
            </div>
          ) : selectedLeague && selectedSeason ? (
            <div className="league-champions-grid">
              {leagueChamps.map((item) => (
                <div key={item.place} className={`league-champion-card place-${item.place}`}>
                  <div className="league-champion-card-header">
                    <div className="league-champion-place-badge">
                      <FiAward />
                      <span>{placeLabel(item.place)}</span>
                    </div>
                    <button
                      type="button"
                      className="league-champion-clear-btn"
                      onClick={() => clearChampion(item.place)}
                      disabled={saving || loading}
                    >
                      <FiX />
                    </button>
                  </div>

                  <div className="league-champion-card-body">
                    <div className="league-champion-current-team">
                      {getChampionTeamName(item)}
                    </div>

                    <div className="league-champion-select-wrapper">
                      <label>{t('team')}</label>
                      <SelectSearch
                        options={teamOptions}
                        search
                        value={getChampionTeamId(item)}
                        onChange={(teamId) => handleChampionChange(item.place, teamId)}
                        filterOptions={fuzzySearch}
                        emptyMessage={t('lcTeamNotFound')}
                        placeholder={t('lcTeamPlaceholder')}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="league-champions-empty-state">
              <h3>{t('lcSelectPrompt')}</h3>
              <p>{t('lcSelectHint')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeagueChampionsManagement;